import { getDefaultStore } from 'jotai';
import { api } from '@/lib/api';
import { businessUnitsAtom, resolvedBusinessUnitIdAtom } from '@/hooks/use-business-units';
import type { BusinessUnit } from '@/types';
import { isAccountScopedIntegration } from '@/modules/integrations/provider-kind';

/**
 * Attaching the operator's current brand to an integration lifecycle call.
 *
 * Deliberately NOT an axios interceptor, for the reason src/lib/business-unit-scope.ts
 * already gives for reads: an allow-list buried in the HTTP layer sits far from the rule
 * it governs. This is narrower on every axis — one service, machine's integration routes
 * only, and the rule is the whole file.
 *
 * It is also not threaded through every connect method's parameter list. There are two
 * dozen of them, each with a hand-written positional signature, and the failure mode of
 * missing one is silent: that provider keeps connecting, just to the wrong brand, forever.
 * One choke point is checkable by reading one file.
 *
 * machine's withBusinessUnit reads `body.bu_id ?? query.bu_id`, so the body is enough for
 * a POST and `buParams` covers the OAuth flows that redirect through a GET.
 */

/**
 * The brand the operator currently has selected.
 *
 * The RESOLVED atom, not the stored one. The stored value is a bare string from
 * localStorage that nothing has checked against the account: a brand belongs to exactly one
 * account, and localStorage is per ORIGIN, so a brand left behind by another account on the
 * same origin reads back as a perfectly valid-looking id. `resolvedBusinessUnitIdAtom`
 * matches it against the account's own list and falls back to the default brand when it is
 * not there — the same resolution `useBusinessUnit` performs for every scoped read.
 *
 * Reading the raw one sent account 417's brand 347 on a connect made from account 1, and
 * machine rejected it with "Business unit not found for this account". It resolves to null
 * only before the brand list has loaded, which ProtectedRoute now gates on.
 */
export const currentBuId = (): string | null => {
	const store = getDefaultStore();

	// NEVER a brand this has not checked against the account. `resolvedBusinessUnitIdAtom`
	// hands back the STORED id untouched when the list is empty — reasonable for a read,
	// which then answers account-wide and corrects itself, and wrong here: a write files a
	// row under whatever it is given. localStorage is per ORIGIN, so the id sitting there
	// may belong to an account this session has nothing to do with, and it looks perfectly
	// valid. That is how brand 347 (account 417) went out on a connect from account 447.
	//
	// No list means no answer, not a guess. ProtectedRoute gates on the list before anything
	// renders, so this is the floor rather than the usual path.
	const units = (store.get(businessUnitsAtom).data ?? []) as BusinessUnit[];
	if (units.length === 0) return null;

	return store.get(resolvedBusinessUnitIdAtom);
};

/**
 * `{ bu_id }` when a brand is selected, and nothing at all when it is not.
 *
 * ABSENT rather than null on purpose: machine's resolveBuId treats an absent or empty
 * bu_id as "no brand named" and a write then lands on the account's default. A literal
 * null in JSON reaches the same branch only by luck of how it is coerced, which is not a
 * thing to rely on for which store a connection opens.
 */
export const buParams = (): { bu_id?: string } => {
	const buId = currentBuId();
	return buId ? { bu_id: buId } : {};
};

/**
 * The same, for a named provider: nothing at all when that provider is account-scoped.
 *
 * A helpdesk belongs to the ACCOUNT, which is what provider-kind.ts says one line away and
 * what kim-tool-server and machine both enforce — machine's `effectiveBuId` nulls the
 * column for a helpdesk on write. So Richpanel, Gladly, Kustomer and Zendesk were sending a
 * brand that was accepted, ignored and discarded, while Gorgias, Freshdesk, Zoho and
 * HelpScout sent none. Nothing broke, and that is the problem: four files disagreed about
 * the rule and the disagreement was invisible because the receiver papered over it. The one
 * that eventually acts on it would be a silent misroute.
 *
 * Pass the provider at any call site that connects one. Omitting it keeps the brand, which
 * is right for the two dozen brand-scoped integrations that are the majority here.
 */
export const buParamsFor = (provider?: string): { bu_id?: string } =>
	provider != null && isAccountScopedIntegration(provider) ? {} : buParams();

/**
 * POST to a machine integration route with the current brand attached.
 *
 * `object` rather than `Record<string, unknown>`: several connect payloads are
 * declared interfaces (RichpanelConnectPayload, GladlyConnectPayload), and an
 * interface has no index signature, so requiring one would force a cast at each
 * of those call sites. Spreading is all this needs.
 *
 * `provider` is optional and only the helpdesks need it — see `buParamsFor`.
 */
export const machineConnectPost = <T = unknown>(path: string, body: object = {}, provider?: string) =>
	api.post<T>('/machine' + path, { ...body, ...buParamsFor(provider) });
